import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLMS } from '../../context/LMSContext';
import { db } from '../../lib/supabase';
import { generateSubmissionsPdf } from '../../lib/submissionsPdf';
import { useIdleTimeoutControls } from '../../context/IdleTimeoutContext';

// Above this many submissions for one student, the export is slow enough that
// they should be told before it starts. Measured: a 104-image student took
// 174s, so roughly 1.7s per image; 40 submissions is about a minute in.
const LARGE_EXPORT_THRESHOLD = 40;

const AssignmentGrid = () => {
  const {
    currentUser,
    cohorts,
    modules,
    selectedCohortId,
    setSelectedCohortId,
    getUsersForCohort
  } = useLMS();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [gradeForm, setGradeForm] = useState({
    grade: '',
    pointsEarned: '',
    feedback: '',
    status: 'graded'
  });

  // Filters
  const [selectedWeek, setSelectedWeek] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // PDF export
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [exportError, setExportError] = useState('');
  const [showLargeExportModal, setShowLargeExportModal] = useState(false);
  const [pendingExportStudent, setPendingExportStudent] = useState(null);

  // The export runs for minutes without any input events, so hold the
  // inactivity countdown while it does.
  const { suspend: suspendIdleTimeout, resume: resumeIdleTimeout } = useIdleTimeoutControls();

  const students = selectedCohortId 
    ? getUsersForCohort(selectedCohortId).filter(s => s.role === 'student') 
    : [];

  // Get all assignment-type items from modules
  const assignmentItems = useMemo(() => {
    const items = [];
    modules.forEach(module => {
      const weekNum = module.weekNumber || module.week_number;
      (module.items || []).forEach(item => {
        if (['assignment', 'quiz', 'exam', 'workbook'].includes(item.type)) {
          items.push({
            ...item,
            weekNumber: weekNum,
            moduleTitle: module.title
          });
        }
      });
    });
    return items;
  }, [modules]);

  // Get unique weeks for filter
  const availableWeeks = useMemo(() => {
    const weeks = [...new Set(assignmentItems.map(i => i.weekNumber))];
    return weeks.sort((a, b) => a - b);
  }, [assignmentItems]);

  // Filter assignments by selected week
  const filteredAssignments = useMemo(() => {
    if (selectedWeek === 'all') return assignmentItems;
    return assignmentItems.filter(i => i.weekNumber === parseInt(selectedWeek));
  }, [assignmentItems, selectedWeek]);

  // Filter students by search, sorted by last name
  const filteredStudents = useMemo(() => {
    let result = [...students].sort((a, b) => {
      const aLast = (a.name || '').trim().split(/\s+/).slice(-1)[0] || '';
      const bLast = (b.name || '').trim().split(/\s+/).slice(-1)[0] || '';
      return aLast.localeCompare(bLast);
    });

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => (s.name || '').toLowerCase().includes(q));
    }

    return result;
  }, [students, searchQuery]);

  // Load submissions when cohort changes
  useEffect(() => {
    if (selectedCohortId) {
      loadSubmissions();
    }
  }, [selectedCohortId]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const { data, error } = await db.getCohortSubmissions(selectedCohortId);
      if (data && !error) {
        setSubmissions(data);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
    setLoading(false);
  };

  // Create a lookup map for submissions
  const submissionMap = useMemo(() => {
    const map = {};
    submissions.forEach(sub => {
      const key = `${sub.student_id}-${sub.module_item_id}`;
      map[key] = sub;
    });
    return map;
  }, [submissions]);

  const getSubmission = (studentId, itemId) => {
    return submissionMap[`${studentId}-${itemId}`] || null;
  };

  // Filter students by submission status
  const displayStudents = useMemo(() => {
    if (statusFilter === 'all') return filteredStudents;

    return filteredStudents.filter(student => {
      return filteredAssignments.some(item => {
        const sub = getSubmission(student.id, item.id);
        if (statusFilter === 'needs-review') return sub?.status === 'submitted';
        if (statusFilter === 'graded') return sub?.status === 'graded';
        if (statusFilter === 'not-submitted') return !sub;
        if (statusFilter === 'returned') return sub?.status === 'returned';
        return true;
      });
    });
  }, [filteredStudents, filteredAssignments, statusFilter, submissionMap]);

  // Per-student assignment completion status for the currently filtered assignments
  const getStudentCompletionStatus = (studentId) => {
    const total = filteredAssignments.length;
    if (total === 0) return { submitted: 0, total: 0, allDone: true };
    const submitted = filteredAssignments.filter(item => {
      const sub = getSubmission(studentId, item.id);
      return sub != null; // any submission status counts as submitted
    }).length;
    return { submitted, total, allDone: submitted >= total };
  };

  // =========================================================================
  // PDF EXPORT
  // Pulls whatever the current Week / Show filters have narrowed the grid to
  // and writes it out as a packet: typed submissions inline, photo uploads
  // embedded, everything else listed with its download link.
  // =========================================================================
  const cohortName = useMemo(
    () => cohorts.find(c => c.id === selectedCohortId)?.name || 'Cohort',
    [cohorts, selectedCohortId]
  );

  // Attach the stored file records to a set of submissions. Falls back to the
  // legacy single-file columns for submissions made before submission_files.
  const attachFiles = async (subs) => {
    const map = {};
    const ids = subs.filter(s => s).map(s => s.id);

    try {
      const { data } = await db.getSubmissionFilesForSubmissions(ids);
      (data || []).forEach(file => {
        if (!map[file.submission_id]) map[file.submission_id] = [];
        map[file.submission_id].push(file);
      });
    } catch (err) {
      console.error('Error loading submission files:', err);
    }

    subs.forEach(sub => {
      if (!sub) return;
      if (!map[sub.id] || map[sub.id].length === 0) {
        map[sub.id] = sub.file_url
          ? [{
              id: `${sub.id}-legacy`,
              file_url: sub.file_url,
              file_name: sub.file_name,
              file_size: sub.file_size,
              file_type: sub.file_type
            }]
          : [];
      }
    });

    return map;
  };

  const exportPdf = async (studentList, { reportTitle, fileName }) => {
    if (exporting) return;

    if (studentList.length === 0 || filteredAssignments.length === 0) {
      alert('Nothing to export for the current filters.');
      return;
    }

    setExporting(true);
    setExportError('');
    setExportProgress('Gathering submissions...');
    suspendIdleTimeout();

    try {
      const relevant = [];
      studentList.forEach(student => {
        filteredAssignments.forEach(item => {
          const sub = getSubmission(student.id, item.id);
          if (sub) relevant.push(sub);
        });
      });

      const filesBySubmission = await attachFiles(relevant);

      const entries = studentList.map(student => ({
        student,
        rows: filteredAssignments.map(item => {
          const submission = getSubmission(student.id, item.id);
          return {
            item,
            submission,
            files: submission ? filesBySubmission[submission.id] || [] : []
          };
        })
      }));

      const weekLabel = selectedWeek === 'all' ? 'All Weeks' : `Week ${selectedWeek}`;

      await generateSubmissionsPdf(entries, {
        reportTitle,
        generatedFor: `${cohortName} · ${weekLabel}`,
        fileName,
        onProgress: (done, total) => {
          setExportProgress(`Building PDF... ${done} of ${total}`);
        }
      });
    } catch (err) {
      console.error('Error exporting submissions PDF:', err);
      // Surface it in the toolbar as well as the alert: an alert dismissed by
      // reflex used to leave no trace that the export had failed at all.
      setExportError(err.message || String(err));
      alert(`Failed to build the PDF: ${err.message || err}`);
    } finally {
      // finally, so a thrown error cannot leave the idle timeout disabled.
      resumeIdleTimeout();
      setExporting(false);
      setExportProgress('');
    }
  };

  // How many submissions one student has in the current Week / Show scope.
  // That count, not the student count, is what makes an export slow.
  const countForStudent = (student) => filteredAssignments.reduce(
    (n, item) => n + (getSubmission(student.id, item.id) ? 1 : 0),
    0
  );

  // The cohort-wide export is deliberately gone: at 31 students it stalled
  // partway through and the client does not want it. Per student is the
  // supported path, and it respects the Week filter.
  const exportStudent = (student) => {
    exportPdf([student], {
      reportTitle: 'Assignment Submissions',
      fileName: `Submissions_${(student.name || 'Student').replace(/\s+/g, '_')}.pdf`
    });
  };

  // Past the threshold, say what is being asked for before starting.
  const requestExportStudent = (student) => {
    if (countForStudent(student) > LARGE_EXPORT_THRESHOLD) {
      setPendingExportStudent(student);
      setShowLargeExportModal(true);
      return;
    }
    exportStudent(student);
  };

  const confirmLargeExport = () => {
    const student = pendingExportStudent;
    setShowLargeExportModal(false);
    setPendingExportStudent(null);
    if (student) exportStudent(student);
  };

  // Single submission straight out of the grading modal, with whatever files
  // that modal already loaded.
  const exportSelectedSubmission = async () => {
    if (!selectedSubmission || exporting) return;
    setExporting(true);
    setExportError('');
    suspendIdleTimeout();
    try {
      const item = assignmentItems.find(i => i.id === selectedSubmission.module_item_id)
        || selectedSubmission.module_item
        || { title: 'Assignment' };
      const student = selectedSubmission.student || { name: 'Student' };

      await generateSubmissionsPdf(
        [{ student, rows: [{ item, submission: selectedSubmission, files: submissionFiles }] }],
        {
          reportTitle: item.title || 'Assignment Submission',
          generatedFor: cohortName,
          fileName: `Submission_${(student.name || 'Student').replace(/\s+/g, '_')}.pdf`
        }
      );
    } catch (err) {
      console.error('Error exporting submission PDF:', err);
      setExportError(err.message || String(err));
      alert(`Failed to build the PDF: ${err.message || err}`);
    } finally {
      resumeIdleTimeout();
      setExporting(false);
    }
  };

  // Refs for synced top/bottom scrollbars
  const gridScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(0);
  const isSyncing = useRef(false);

  // Measure table width for top scrollbar
  useEffect(() => {
    if (!tableRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTableWidth(entry.target.scrollWidth);
      }
    });
    observer.observe(tableRef.current);
    return () => observer.disconnect();
  }, [filteredAssignments, displayStudents]);

  // Sync scroll positions between top and grid scrollbars
  const handleGridScroll = useCallback(() => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    if (topScrollRef.current && gridScrollRef.current) {
      topScrollRef.current.scrollLeft = gridScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => { isSyncing.current = false; });
  }, []);

  const handleTopScroll = useCallback(() => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    if (gridScrollRef.current && topScrollRef.current) {
      gridScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => { isSyncing.current = false; });
  }, []);

  const getStatusClass = (submission) => {
    if (!submission) return 'st-none';
    const map = {
      draft: 'st-draft',
      submitted: 'st-submitted',
      reviewed: 'st-reviewed',
      graded: 'st-graded',
      returned: 'st-returned'
    };
    return map[submission.status] || 'st-draft';
  };

  const getStatusLabel = (submission) => {
    if (!submission) return 'Not submitted';
    const map = {
      draft: 'Draft',
      submitted: 'Submitted',
      reviewed: 'Reviewed',
      graded: 'Graded',
      returned: 'Returned'
    };
    return map[submission.status] || 'Draft';
  };

  const openGradeModal = async (submission) => {
    setSelectedSubmission(submission);
    setSubmissionFiles([]);
    setGradeForm({
      grade: submission.grade || '',
      pointsEarned: submission.points_earned || '',
      feedback: submission.feedback || '',
      status: submission.status === 'graded' ? 'graded' : 'graded'
    });
    setShowGradeModal(true);

    // Load multi-file records
    try {
      const { data: files } = await db.getSubmissionFiles(submission.id);
      if (files && files.length > 0) {
        setSubmissionFiles(files);
      } else if (submission.file_url) {
        setSubmissionFiles([{
          id: 'legacy',
          file_url: submission.file_url,
          file_name: submission.file_name,
          file_size: submission.file_size,
          file_type: submission.file_type
        }]);
      }
    } catch (_) {
      if (submission.file_url) {
        setSubmissionFiles([{
          id: 'legacy',
          file_url: submission.file_url,
          file_name: submission.file_name,
          file_size: submission.file_size,
          file_type: submission.file_type
        }]);
      }
    }
  };

  const handleGrade = async (e) => {
    e.preventDefault();
    
    try {
      const { data, error } = await db.gradeSubmission(selectedSubmission.id, {
        grade: parseInt(gradeForm.grade) || null,
        pointsEarned: parseInt(gradeForm.pointsEarned) || null,
        feedback: gradeForm.feedback,
        status: gradeForm.status,
        reviewedBy: currentUser.id
      });

      if (error) {
        alert('Failed to save grade: ' + error.message);
        return;
      }

      setSubmissions(prev => prev.map(s => 
        s.id === selectedSubmission.id ? { ...s, ...data } : s
      ));
      
      setShowGradeModal(false);
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Grading error:', error);
      alert('Failed to save grade.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalPossible = students.length * assignmentItems.length;
    const submitted = submissions.filter(s => s.status !== 'draft').length;
    const graded = submissions.filter(s => s.status === 'graded').length;
    const needsReview = submissions.filter(s => s.status === 'submitted').length;
    
    return { totalPossible, submitted, graded, needsReview };
  }, [students.length, assignmentItems.length, submissions]);

  const getTypeIcon = (type) => {
    const icons = { assignment: '📝', quiz: '❓', exam: '📋', workbook: '📚' };
    return icons[type] || '📄';
  };

  return (
    <div className="ag-view">
      <div className="ag-top-bar">
        <div className="ag-top-left">
          <h1>Assignment Submissions</h1>
          <p>Review and grade student submissions</p>
        </div>
        <div className="ag-top-right">
          <select 
            value={selectedCohortId || ''} 
            onChange={(e) => setSelectedCohortId(e.target.value)}
            className="ag-cohort-select"
          >
            <option value="">Choose a cohort...</option>
            {cohorts.map(cohort => (
              <option key={cohort.id} value={cohort.id}>
                {cohort.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedCohortId && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Select a Cohort</h3>
          <p>Choose a cohort above to view assignment submissions</p>
        </div>
      )}

      {selectedCohortId && loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading submissions...</p>
        </div>
      )}

      {selectedCohortId && !loading && (
        <>
          {/* Stats */}
          <div className="ag-stats">
            <div className="ag-stat-card">
              <span className="ag-stat-val">{stats.submitted}</span>
              <span className="ag-stat-lbl">Submitted</span>
            </div>
            <div className="ag-stat-card warn">
              <span className="ag-stat-val">{stats.needsReview}</span>
              <span className="ag-stat-lbl">Needs Review</span>
            </div>
            <div className="ag-stat-card">
              <span className="ag-stat-val">{stats.graded}</span>
              <span className="ag-stat-lbl">Graded</span>
            </div>
            <div className="ag-stat-card">
              <span className="ag-stat-val">
                {stats.totalPossible > 0 
                  ? Math.round((stats.submitted / stats.totalPossible) * 100) 
                  : 0}%
              </span>
              <span className="ag-stat-lbl">Completion</span>
            </div>
          </div>

          {/* Toolbar */}
          <div className="ag-toolbar">
            <div className="ag-filters">
              <div className="ag-filter-item">
                <label>Week</label>
                <select value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)}>
                  <option value="all">All Weeks</option>
                  {availableWeeks.map(w => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
              </div>
              <div className="ag-filter-item">
                <label>Show</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Students</option>
                  <option value="needs-review">Needs Review</option>
                  <option value="not-submitted">Missing Submissions</option>
                  <option value="graded">Graded</option>
                  <option value="returned">Returned</option>
                </select>
              </div>
            </div>
            <div className="ag-search-wrap">
              <input 
                type="text" 
                placeholder="Search students..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="ag-search"
              />
              {searchQuery && (
                <button className="ag-search-clear" onClick={() => setSearchQuery('')}>✕</button>
              )}
            </div>
            <div className="ag-export-wrap" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
              {exportError && !exporting && (
                <span style={{ fontSize: '12px', color: '#b91c1c' }} title={exportError}>
                  Export failed: {exportError}
                </span>
              )}
              {exporting && (
                <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {exportProgress || 'Building PDF...'}
                </span>
              )}
              <span style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                Use ⬇ next to a student to download their submissions
              </span>
            </div>
          </div>

          {/* Grid */}
          {displayStudents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>No Results</h3>
              <p>{students.length === 0 ? 'No students are enrolled in this cohort' : 'No students match your filters'}</p>
            </div>
          ) : filteredAssignments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h3>No Assignments</h3>
              <p>No assignment items found for the selected week</p>
            </div>
          ) : (
            <div className="ag-grid-container">
              {/* Top scrollbar */}
              <div className="ag-top-scrollbar" ref={topScrollRef} onScroll={handleTopScroll}>
                <div style={{ width: tableWidth, height: 1 }} />
              </div>
              <div className="ag-grid-scroll" ref={gridScrollRef} onScroll={handleGridScroll}>
                <table className="ag-table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th className="ag-th-student">
                        Student ({displayStudents.length})
                      </th>
                      <th className="ag-th-item" style={{ minWidth: '90px', textAlign: 'center' }}>
                        <div className="ag-th-inner">
                          <span className="ag-th-title">Status</span>
                        </div>
                      </th>
                      {filteredAssignments.map(item => (
                        <th key={item.id} className="ag-th-item">
                          <div className="ag-th-inner" title={item.title}>
                            <span className="ag-th-icon">{getTypeIcon(item.type)}</span>
                            <span className="ag-th-title">{item.title}</span>
                            <span className="ag-th-meta">W{item.weekNumber} &bull; {item.points || 0}pts</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayStudents.map(student => (
                      <tr key={student.id}>
                        <td className="ag-td-student">
                          <div className="ag-student-cell">
                            {student.avatar_url ? (
                              <img src={student.avatar_url} alt={student.name} className="ag-avatar-img" />
                            ) : (
                              <div className="ag-avatar">
                                {(student.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                              </div>
                            )}
                            <span className="ag-student-name">{student.name}</span>
                            <button
                              className="btn-icon"
                              onClick={() => requestExportStudent(student)}
                              disabled={exporting}
                              title={`Download ${student.name}'s submissions as PDF`}
                              style={{ marginLeft: 'auto', fontSize: '12px', lineHeight: 1, opacity: exporting ? 0.5 : 1 }}
                            >
                              ⬇
                            </button>
                          </div>
                        </td>
                        <td className="ag-td-cell" style={{ textAlign: 'center', padding: '4px 6px' }}>
                          {(() => {
                            const status = getStudentCompletionStatus(student.id);
                            if (status.total === 0) return null;
                            return (
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                backgroundColor: status.allDone ? '#ecfdf5' : '#fef3c7',
                                color: status.allDone ? '#065f46' : '#92400e',
                                border: `1px solid ${status.allDone ? '#a7f3d0' : '#fde68a'}`
                              }}>
                                <span>{status.allDone ? '✅' : '⚠️'}</span>
                                <span>{status.submitted}/{status.total}</span>
                              </div>
                            );
                          })()}
                        </td>
                        {filteredAssignments.map(item => {
                          const submission = getSubmission(student.id, item.id);
                          const stClass = getStatusClass(submission);
                          const stLabel = getStatusLabel(submission);
                          
                          return (
                            <td
                              key={item.id}
                              className={`ag-td-cell ${stClass} ${submission ? 'clickable' : ''}`}
                              onClick={() => submission && openGradeModal(submission)}
                              title={`${student.name} - ${item.title}: ${stLabel}${submission?.is_late ? ' (Late)' : ''}`}
                            >
                              <div className="ag-cell-inner">
                                {submission?.status === 'graded' && submission.grade !== null && submission.grade !== undefined ? (
                                  <span className="ag-cell-grade">{submission.grade}</span>
                                ) : (
                                  <span className="ag-cell-dot"></span>
                                )}
                                {submission?.is_late && (
                                  <span className="ag-late-badge" style={{
                                    position: 'absolute',
                                    top: '2px',
                                    right: '2px',
                                    fontSize: '8px',
                                    fontWeight: '700',
                                    color: '#fff',
                                    backgroundColor: '#ef4444',
                                    borderRadius: '3px',
                                    padding: '1px 3px',
                                    lineHeight: '1.2',
                                    letterSpacing: '0.02em'
                                  }}>L</span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div className="ag-legend">
                <span className="ag-legend-item"><span className="ag-legend-dot st-none"></span> Not submitted</span>
                <span className="ag-legend-item"><span className="ag-legend-dot st-submitted"></span> Submitted</span>
                <span className="ag-legend-item"><span className="ag-legend-dot st-graded"></span> Graded</span>
                <span className="ag-legend-item"><span className="ag-legend-dot st-returned"></span> Returned</span>
                <span className="ag-legend-item"><span className="ag-legend-dot st-reviewed"></span> Reviewed</span>
                <span className="ag-legend-item"><span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#ef4444', fontSize: '7px', color: '#fff', fontWeight: '700', textAlign: 'center', lineHeight: '12px', marginRight: '4px', verticalAlign: 'middle' }}>L</span> Late</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Large export confirmation */}
      {showLargeExportModal && (
        <div
          className="modal-overlay"
          onClick={() => { setShowLargeExportModal(false); setPendingExportStudent(null); }}
        >
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Large export</h3>
              <button
                className="modal-close"
                onClick={() => { setShowLargeExportModal(false); setPendingExportStudent(null); }}
              >×</button>
            </div>
            <div className="modal-body">
              <p style={{ margin: '0 0 12px', lineHeight: '1.5' }}>
                This will build a PDF from{' '}
                <strong>
                  {pendingExportStudent ? countForStudent(pendingExportStudent) : 0} submissions
                </strong>{' '}
                for {pendingExportStudent?.name || 'this student'}. Photo attachments are downloaded
                and embedded one at a time, so an export this size takes a few minutes. Leave the tab
                open while it runs.
              </p>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '13px', lineHeight: '1.5' }}>
                Narrowing the Week filter first gives you a faster, smaller PDF per week.
              </p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                className="btn-secondary"
                onClick={() => { setShowLargeExportModal(false); setPendingExportStudent(null); }}
              >
                Cancel
              </button>
              <button className="btn-primary" onClick={confirmLargeExport}>
                Export anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grade Modal - unchanged */}
      {showGradeModal && selectedSubmission && (
        <div className="modal-overlay" onClick={() => setShowGradeModal(false)}>
          <div className="modal grade-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Grade Submission</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  className="btn-secondary"
                  onClick={exportSelectedSubmission}
                  disabled={exporting}
                  style={{ fontSize: '12px' }}
                >
                  {exporting ? 'Building...' : '⬇ PDF'}
                </button>
                <button className="modal-close" onClick={() => setShowGradeModal(false)}>×</button>
              </div>
            </div>

            <div className="modal-body">
              <div className="submission-info">
                <div className="info-row">
                  <span className="info-label">Student:</span>
                  <span className="info-value">{selectedSubmission.student?.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Assignment:</span>
                  <span className="info-value">{selectedSubmission.module_item?.title}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Submitted:</span>
                  <span className="info-value">
                    {formatDate(selectedSubmission.submitted_at)}
                    {selectedSubmission.is_late && (
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        border: '1px solid #fecaca'
                      }}>Late</span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Max Points:</span>
                  <span className="info-value">{selectedSubmission.module_item?.points || 'N/A'}</span>
                </div>
              </div>

              <div className="submission-content-view">
                <h4>Submission</h4>
                {selectedSubmission.submission_type === 'text' && (
                  <div className="content-box text-content">
                    {selectedSubmission.text_content || 'No text content'}
                  </div>
                )}
                {selectedSubmission.submission_type === 'file' && (
                  <div className="content-box file-content">
                    {submissionFiles.length > 0 ? (
                      <div className="file-list">
                        {submissionFiles.map((file) => (
                          <div key={file.id} className="file-list-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
                            <span className="file-icon">📄</span>
                            <span className="file-name">{file.file_name}</span>
                            {file.file_size && <span className="file-size" style={{ color: '#888', fontSize: '0.85em' }}>{file.file_size}</span>}
                            <a 
                              href={file.file_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn-secondary small"
                            >
                              View/Download
                            </a>
                          </div>
                        ))}
                        <p style={{ color: '#888', fontSize: '0.85em', marginTop: '4px' }}>{submissionFiles.length} file{submissionFiles.length !== 1 ? 's' : ''} attached</p>
                      </div>
                    ) : (
                      <>
                        <span className="file-icon">📄</span>
                        <span className="file-name">{selectedSubmission.file_name}</span>
                        <span className="file-size">{selectedSubmission.file_size}</span>
                        <a 
                          href={selectedSubmission.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-secondary small"
                        >
                          View/Download
                        </a>
                      </>
                    )}
                  </div>
                )}
                {selectedSubmission.submission_type === 'link' && (
                  <div className="content-box link-content">
                    <a 
                      href={selectedSubmission.link_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {selectedSubmission.link_url}
                    </a>
                  </div>
                )}
              </div>

              <form onSubmit={handleGrade} className="grade-form">
                <h4>Grading</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Grade (0-100)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={gradeForm.grade}
                      onChange={(e) => setGradeForm({ ...gradeForm, grade: e.target.value })}
                      placeholder="e.g., 85"
                    />
                  </div>
                  <div className="form-group">
                    <label>Points Earned</label>
                    <input
                      type="number"
                      min="0"
                      max={selectedSubmission.module_item?.points || 100}
                      value={gradeForm.pointsEarned}
                      onChange={(e) => setGradeForm({ ...gradeForm, pointsEarned: e.target.value })}
                      placeholder={`Max: ${selectedSubmission.module_item?.points || 'N/A'}`}
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={gradeForm.status}
                      onChange={(e) => setGradeForm({ ...gradeForm, status: e.target.value })}
                    >
                      <option value="reviewed">Reviewed</option>
                      <option value="graded">Graded</option>
                      <option value="returned">Return for Revision</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Feedback</label>
                  <textarea
                    rows={4}
                    value={gradeForm.feedback}
                    onChange={(e) => setGradeForm({ ...gradeForm, feedback: e.target.value })}
                    placeholder="Enter feedback for the student..."
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowGradeModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Save Grade
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentGrid;
